import React from 'react';
import { hasPermission, hasAllPermissions } from '../../lib/permissions';

/**
 * PermissionGuard Component
 * 
 * Props:
 * - required: string|string[] - Required permission(s)
 * - fallback: ReactNode - What to render if permission denied
 * - children: ReactNode - What to render if permission granted
 * - userPermissions: string[] - User's permissions (optional, will fetch from storage)
 * - requireAll: boolean - If true, requires ALL permissions; if false, requires ANY permission (default: false)
 * - silent: boolean - If true, don't show error messages for permission failures
 */
const PermissionGuard = ({ 
  required, 
  fallback = null, 
  children, 
  userPermissions,
  requireAll = false,
  silent = false
}) => {
  const permissionsFromPropsOrStorage = Array.isArray(userPermissions)
    ? userPermissions
    : (() => {
        try {
          const raw = localStorage.getItem('permissions');
          const parsed = raw ? JSON.parse(raw) : null;
          return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          return [];
        }
      })();

  // Use hasAllPermissions if requireAll is true, otherwise use hasPermission (ANY)
  const isAllowed = requireAll 
    ? hasAllPermissions(permissionsFromPropsOrStorage, required)
    : hasPermission(permissionsFromPropsOrStorage, required);

  if (!isAllowed) {
    // Log permission denial for debugging (but only if not silent)
    if (!silent) {
      console.warn('Permission denied:', {
        required,
        userPermissions: permissionsFromPropsOrStorage,
        requireAll
      });
    }
    return fallback;
  }

  return <>{children}</>;
};

export default PermissionGuard;


